import { useEffect, useState } from "react";
import { useExamList, useGrades, useGradeSubjects, useUserInfo } from "../../react-query/queries";
import { useAuth } from "../../utils/Auth/AuthProvider";
import { ListGroup, Table, Pagination, Label, Select, Modal, Button, Spinner, TextInput } from "flowbite-react";
import { useNavigate } from "react-router-dom";
import { HiOutlineArrowRight, HiOutlineDocumentDuplicate, HiOutlineTrash, HiOutlinePencilAlt, HiOutlineSwitchHorizontal, HiOutlineExclamationCircle } from "react-icons/hi";
import { AdminAcademicYear, AdminField, AdminSchool, adminCreateField, adminCreateGrade, adminCreateSubject, adminExamTransfer, adminGetFieldAcademicYears, adminGetSchoolGrades, adminGetSchools, adminCreateAcademicYear, adminGetInstitutionFields, adminGetGradeSubjects, adminGetAcademicYearCourses, adminGetExams, deleteExam } from "../../utils/remote";

type QExam = {
    id: number,
    name: string,
    no_ques: string,
    price: string,
    status: string,
    sub: string,
    time: number,
    time_given: number,
    grade: string,
    institution_name?: string,
}

function ExamListComponent() {
    const { token, role } = useAuth();
    const { data: userInfo } = useUserInfo();
    const [nowGrade, setNowGrade] = useState(0)
    const [nowSubject, setNowSubject] = useState('0')
    const [examsArray, setExamsArray] = useState<any[]>([]);
    const [currentPage, setCurrentPage] = useState(1);
    const navigate = useNavigate();

    const isAllowed = !!token && (role === 'superuser' || role === 'school' || role === 'college_university');


    const { data: gradeData } = useGrades(token)
    const { data: subjectData, isFetched: subjectIsFetched } = useGradeSubjects(token, nowGrade.toString())
    const [fieldsData, setFieldsData] = useState<AdminField[] | null>(null);
    const [search, setSearch] = useState('');
    const { status: examsStatus, data: examsData, isFetching: examsIsFetching, refetch: examRefetch } = useExamList(token, `${currentPage}`, nowGrade.toString(), nowSubject.toString(), search)
    // For university/college flow we will use the adminGetExams endpoint instead of the school exam-list endpoint
    const [adminExamsData, setAdminExamsData] = useState<{ exams: any[]; total_count: number } | null>(null);

    const [isTransferModalOpen, setIsTransferModalOpen] = useState(false);
    const [selectedExamForTransfer, setSelectedExamForTransfer] = useState<QExam | null>(null);

    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [examToDelete, setExamToDelete] = useState<QExam | null>(null);
    const [isDeleting, setIsDeleting] = useState(false);

    if (!isAllowed) {
        return (
            <div className="grid grid-cols-12 gap-5">
                <div className="col-span-12">
                    <div className="w-full bg-white px-5 pt-6 pb-2.5 shadow-default dark:border-strokedark dark:bg-boxdark sm:px-7.5 xl:pb-1">
                        <div className="max-w-full overflow-x-auto">
                            <h4 className="mb-6 text-xl font-semibold text-black dark:text-white">List of Exams</h4>
                            <p className="text-red-600">403 - Not authorized</p>
                        </div>
                    </div>
                </div>
            </div>
        )
    }

    const onPageChange = (page: number) => {
        setCurrentPage(page)
    };



    useEffect(() => {
        if (isAllowed) {
            examRefetch();
        }
    }, [currentPage, nowGrade, nowSubject, search, isAllowed])

    // Fetch fields of study (departments) when the user's institution is a university/college
    useEffect(() => {
        let mounted = true;
        const instType = userInfo?.institution_type || (role === 'college_university' ? 'University/College' : (role === 'school' ? 'School' : null));
        const instId = userInfo?.institution_id ?? null;
        if (instType && instId && (instType.toLowerCase().includes('university') || instType.toLowerCase().includes('college'))) {
            (async () => {
                try {
                    const fetched = await adminGetInstitutionFields(instId);
                    if (mounted) setFieldsData(fetched);
                } catch (err) {
                    console.error('Failed to load institution fields', err);
                    if (mounted) setFieldsData([]);
                }
            })();
        } else {
            // Clear fieldsData for non-university institutions
            setFieldsData(null);
        }
        return () => { mounted = false; }
    }, [userInfo, role]);

    // Helper to explicitly load academic years for a given field (department)
    const handleDeleteExam = async () => {
        if (!examToDelete) return;
        setIsDeleting(true);
        try {
            await deleteExam(token, examToDelete.id);
            setIsDeleteModalOpen(false);
            setExamToDelete(null);
            examRefetch();
        } catch (error) {
            console.error("Failed to delete exam", error);
            alert("Failed to delete exam. Please try again.");
        } finally {
            setIsDeleting(false);
        }
    };

    const loadAcademicYearsForField = async (fieldId: number) => {
        const instId = userInfo?.institution_id ?? null;
        if (!instId) return;
        try {
            // Clear previous values while loading
            setFilterAcademicYears(null);
            setFilterCourses(null);
            setFilterAcademicYearId(null); // Do not auto-select an academic year when a department is clicked; let the user choose the year explicitly.
            const years = await adminGetFieldAcademicYears(instId, fieldId);
            setFilterAcademicYears(years);
            setFilterAcademicYearId(null);
        } catch (err) {
            console.error('Failed to load academic years', err);
            setFilterAcademicYears([]);
            setFilterAcademicYearId(null);
        }
    }

    // States and effects for university filtering: academic years -> courses
    const [filterAcademicYears, setFilterAcademicYears] = useState<AdminAcademicYear[] | null>(null);
    const [filterCourses, setFilterCourses] = useState<{ id: number; name: string }[] | null>(null);
    const [filterAcademicYearId, setFilterAcademicYearId] = useState<number | null>(null);

    // When a department/field (nowGrade) is selected for a university, fetch academic years for that field
    useEffect(() => {
        let mounted = true;
        const instId = userInfo?.institution_id ?? null;
        if (fieldsData != null && nowGrade && instId) {
            (async () => {
                try {
                    const years = await adminGetFieldAcademicYears(instId, nowGrade);
                    if (mounted) {
                        setFilterAcademicYears(years);
                        setFilterAcademicYearId(null); // Do not auto-select the academic year; user should pick one
                    }
                } catch (err) {
                    console.error('Failed to load academic years', err);
                    if (mounted) setFilterAcademicYears([]);
                }
            })();
        } else {
            setFilterAcademicYears(null);
            setFilterAcademicYearId(null);
            setFilterCourses(null);
        }
        return () => { mounted = false; }
    }, [fieldsData, nowGrade, userInfo]);

    // Fetch exams via admin endpoint for university/college institutions
    useEffect(() => {
        let mounted = true;
        const instId = userInfo?.institution_id ?? null;
        // Only use admin endpoint when fieldsData is present (we treat this as university flow)
        if (fieldsData != null && isAllowed && instId) {
            (async () => {
                try {
                    // indicate fetch in-progress if needed (kept minimal)
                    const pageNum = Number(currentPage) || 1;
                    const gradeParam = nowGrade && nowGrade !== 0 ? nowGrade : null;
                    const subjectParam = nowSubject && nowSubject !== '0' ? Number(nowSubject) : null;
                    const res = await adminGetExams({ page: pageNum, page_size: 10, search: search || undefined, institution: instId, grade: gradeParam ?? null, subject: subjectParam ?? null });
                    if (!mounted) return;
                    const results = Array.isArray(res.results) ? res.results : (res.results || []);
                    // Map admin response to the shape expected by the component (exams + total_count)
                    const mapped = (results || []).map((e: any) => ({ id: e.id, name: e.name, sub: e.subject_name ?? e.course_name ?? '', no_ques: e.no_ques ?? e.no_questions ?? 0, time_given: e.time_given ?? null, grade: e.grade_name ?? e.field_name ?? '', status: e.status, institution_name: e.institution_name ?? '' }));
                    setAdminExamsData({ exams: mapped, total_count: typeof res.count === 'number' ? res.count : mapped.length });
                } catch (err) {
                    console.error('Failed to load admin exams for university flow', err);
                    if (mounted) setAdminExamsData({ exams: [], total_count: 0 });
                } finally {
                    // finished
                }
            })();
        } else {
            // Clear admin exams when not in university flow
            setAdminExamsData(null);
        }
        return () => { mounted = false; }
    }, [fieldsData, nowGrade, filterAcademicYearId, nowSubject, currentPage, search, userInfo, isAllowed]);

    // When an academic year is selected, fetch courses for that year
    useEffect(() => {
        let mounted = true;
        const instId = userInfo?.institution_id ?? null;
        if (fieldsData != null && nowGrade && filterAcademicYearId && instId) {
            (async () => {
                try {
                    const courses = await adminGetAcademicYearCourses(instId, nowGrade, filterAcademicYearId);
                    if (mounted) {
                        setFilterCourses(courses);
                        // reset selected course filter
                        setNowSubject('0');
                    }
                } catch (err) {
                    console.error('Failed to load courses', err);
                    if (mounted) setFilterCourses([]);
                }
            })();
        } else {
            setFilterCourses(null);
            setNowSubject('0');
        }
        return () => { mounted = false; }
    }, [filterAcademicYearId, fieldsData, nowGrade, userInfo]);




    useEffect(() => {
        setExamsArray([])
        try {
            const source = adminExamsData != null ? adminExamsData : examsData;
            const sourceExams = source?.exams ?? [];
            sourceExams.forEach((element: QExam, key: number) => {
                setExamsArray(prevArray => [...prevArray,
                <Table.Row key={key} className="bg-white dark:border-gray-700 dark:bg-gray-800">
                    <Table.Cell className="whitespace-nowrap font-bold text-slate-600 dark:text-white">
                        {element.name}
                    </Table.Cell>
                    <Table.Cell>
                        <li className="list-none font-bold">{element.sub}</li>
                        <li className="list-none">Grade {element.grade}</li>
                    </Table.Cell>
                    <Table.Cell>{element.institution_name || ''}</Table.Cell>
                    <Table.Cell>{element.no_ques}</Table.Cell>
                    <Table.Cell>{element.time_given ? `${element.time_given} minutes` : 'Not assigned'}</Table.Cell>
                    <Table.Cell>{element.status}</Table.Cell>
                    <Table.Cell>
                        <div className="flex items-center space-x-3.5">
                            <button title="View Exam Details" className="hover:text-primary" onClick={()=>navigate(`/exams/exam-detail/${element.id}`)}>
                                <HiOutlinePencilAlt className="h-5 w-5" />
                            </button>
                            <button title="Transfer Exam" className="hover:text-primary" onClick={() => {
                                setSelectedExamForTransfer(element);
                                setIsTransferModalOpen(true);
                            }}>
                                <HiOutlineSwitchHorizontal className="h-5 w-5" />
                            </button>
                            <button title="Delete Exam" className="hover:text-danger" onClick={() => {
                                setExamToDelete(element);
                                setIsDeleteModalOpen(true);
                            }}>
                                <HiOutlineTrash className="h-5 w-5" />
                            </button>
                        </div>
                    </Table.Cell>
                </Table.Row>
                ])
            });
        } catch (error) {
            console.log('no exam yet')
        }

    }, [examsData]);

    return (
        <div className="grid grid-cols-12 gap-5">
            <div className="col-span-3">
                <div className="w-full bg-white px-5 pt-6 pb-2.5 shadow-default dark:border-strokedark dark:bg-boxdark sm:px-7.5 xl:pb-1">
                    <div className="max-w-full overflow-x-auto">
                        <h4 className="mb-6 text-xl font-semibold text-black dark:text-white">
                            { /* Show Departments for universities/colleges */ }
                            {(userInfo?.institution_type || (role === 'college_university' ? 'University/College' : null))?.toString().toLowerCase().includes('university') || (userInfo?.institution_type || '').toString().toLowerCase().includes('college') ? 'Departments' : 'Grades'}
                        </h4>
                    </div>
                    {
                        !isAllowed ? (
                            <p className="text-red-600">403 - Not authorized</p>
                        ) : (
                            // If we have fieldsData, treat this as a university/college and show Departments
                            fieldsData != null ? (
                                fieldsData.length === 0 ? <p>No departments</p> : fieldsData.map((f: AdminField, key: number) => (
                                    <ListGroup.Item key={key} className={`mb-2 cursor-pointer rounded-lg p-3 transition-all duration-200 ease-in-out list-none ${nowGrade == f.id ? 'bg-gray-200 dark:bg-gray-600 text-blue-600 dark:text-blue-400' : 'bg-gray-100 text-gray-800 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600'}`} onClick={async () => {
                                        // Select the department and load its academic years into the filters
                                        setNowSubject('0');
                                        setNowGrade(f.id);
                                        await loadAcademicYearsForField(f.id);
                                    }} active={nowGrade == f.id ? true : false}><span className="text-lg">{f.name}</span></ListGroup.Item>
                                ))
                            ) : (
                                // Fallback to existing grades view for schools
                                gradeData?.grades == null ? <p>Error</p>
                                    : gradeData.grades.length === 0 ? <p>No grades</p>
                                        : gradeData.grades.map((grade: { id: number, grade_name: string }, key: number) => (
                                            <ListGroup.Item key={key} className={`mb-2 cursor-pointer rounded-lg p-3 transition-all duration-200 ease-in-out list-none ${nowGrade == grade.id ? 'bg-gray-200 dark:bg-gray-600 text-blue-600 dark:text-blue-400' : 'bg-gray-100 text-gray-800 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600'}`} onClick={() => {
                                                setNowSubject('0')
                                                setNowGrade(grade.id)
                                            }} active={nowGrade == grade.id ? true : false}><span className="text-lg">Grade {grade.grade_name}</span></ListGroup.Item>
                                        ))
                            )
                        )
                    }
                    <br />
                </div>
            </div>
            <div className="col-span-9">
                <div className="w-full bg-white px-5 pt-6 pb-2.5 shadow-default dark:border-strokedark dark:bg-boxdark sm:px-7.5 xl:pb-1">
                    <div className="max-w-full overflow-x-auto">
                        <h4 className="mb-6 text-xl font-semibold text-black dark:text-white">
                            List of Exams
                        </h4>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end max-w-full md:max-w-3xl">
                            <div>
                                {fieldsData != null ? (
                                    <>
                                        <div className="mb-2 block">
                                            <Label htmlFor="academic-year" value="Filter by Academic Year" />
                                        </div>
                                        <select
                                            id="academic-year"
                                            title='Academic year filter'
                                            value={filterAcademicYearId ?? ''}
                                            onChange={(e) => {
                                                const v = e.target.value ? Number(e.target.value) : null;
                                                setCurrentPage(1);
                                                setFilterAcademicYearId(v);
                                            }}
                                            className='relative z-20 w-full appearance-none rounded border border-stroke bg-transparent py-3 px-4 outline-none transition focus:border-primary active:border-primary dark:border-form-strokedark dark:bg-form-input text-black dark:text-white'
                                        >
                                            <option value={''} className="text-body dark:text-bodydark">Select year</option>
                                            {filterAcademicYears && filterAcademicYears.map((y) => (
                                                <option key={y.id} value={y.id} className="text-body dark:text-bodydark">{y.name}</option>
                                            ))}
                                        </select>

                                        <div className="mt-3 mb-2 block">
                                            <Label htmlFor="course" value="Filter by Course" />
                                        </div>
                                        <select
                                            id="course"
                                            title='Course filter'
                                            value={nowSubject}
                                            onChange={(e) => {
                                                setCurrentPage(1);
                                                setNowSubject(e.target.value);
                                            }}
                                            className='relative z-20 w-full appearance-none rounded border border-stroke bg-transparent py-3 px-4 outline-none transition focus:border-primary active:border-primary dark:border-form-strokedark dark:bg-form-input text-black dark:text-white'
                                        >
                                            <option value={'0'} className="text-body dark:text-bodydark">All</option>
                                            {filterCourses && filterCourses.map((c) => (
                                                <option key={c.id} value={c.id} className="text-body dark:text-bodydark">{c.name}</option>
                                            ))}
                                        </select>
                                    </>
                                ) : (
                                    <>
                                        <div className="mb-2 block">
                                            <Label htmlFor="subject" value="Filter by subject" />
                                        </div>
                                        <select
                                            id="subject"
                                            title='Subject filter'
                                            value={nowSubject}
                                            onChange={(e) => {
                                                setCurrentPage(1);
                                                setNowSubject(e.target.value);
                                            }}
                                            className='relative z-20 w-full appearance-none rounded border border-stroke bg-transparent py-3 px-4 outline-none transition focus:border-primary active:border-primary dark:border-form-strokedark dark:bg-form-input text-black dark:text-white'
                                        >
                                            <option value={'0'} className="text-body dark:text-bodydark">All</option>
                                            {subjectIsFetched ? subjectData.subjects.map((elem: any) => {
                                                return <option key={elem['id']} value={elem['id']} className="text-body dark:text-bodydark">{elem['subject_name']}</option>;
                                            }) : null}
                                        </select>
                                    </>
                                )}
                            </div>
                            <div className="md:col-span-2">
                                <div className="mb-2 block">
                                    <Label htmlFor="exam-search" value="Search exams" />
                                </div>
                                <input
                                    id="exam-search"
                                    type="search"
                                    value={search}
                                    onChange={(e)=>{ setCurrentPage(1); setSearch(e.target.value); }}
                                    placeholder="Search by name or subject..."
                                    className='w-full rounded border border-stroke bg-transparent py-3 px-4 outline-none transition focus:border-primary active:border-primary dark:border-form-strokedark dark:bg-form-input text-black dark:text-white'
                                />
                            </div>
                        </div>

                        <br />
                        <Table hoverable className="exam-table">
                            <Table.Head>
                                <Table.HeadCell>EXAM NAME</Table.HeadCell>
                                <Table.HeadCell>SUBJECT</Table.HeadCell>
                                <Table.HeadCell>INSTITUTE</Table.HeadCell>
                                <Table.HeadCell>No OF QUESTIONS</Table.HeadCell>
                                <Table.HeadCell>TIME(IN MINUTES)</Table.HeadCell>
                                <Table.HeadCell>STATUS</Table.HeadCell>
                                <Table.HeadCell>
                                    <span className="sr-only">Edit</span>
                                </Table.HeadCell>
                            </Table.Head>
                            <Table.Body className="divide-y">
                                    {examsArray}
                                </Table.Body>
                        </Table>
                        <div className="flex overflow-x-auto justify-end">
                            {
                                examsStatus == 'success' ?
                                    <Pagination currentPage={currentPage} totalPages={Math.ceil((adminExamsData?.total_count ?? examsData.total_count ?? 0) / 10) || 1} onPageChange={onPageChange} />
                                    : null
                            }

                        </div>
                        <br />
                    </div>
                </div>
            </div>

            <TransferExamDialog
                isOpen={isTransferModalOpen}
                onClose={() => setIsTransferModalOpen(false)}
                exam={selectedExamForTransfer}
                onTransferSuccess={() => {
                    setIsTransferModalOpen(false);
                    setSelectedExamForTransfer(null);
                    examRefetch(); // Refresh exams list
                }}
            />

            <Modal show={isDeleteModalOpen} size="md" popup onClose={() => !isDeleting && setIsDeleteModalOpen(false)}>
                <Modal.Header />
                <Modal.Body>
                    <div className="text-center">
                        <HiOutlineExclamationCircle className="mx-auto mb-4 h-14 w-14 text-gray-400 dark:text-gray-200" />
                        <h3 className="mb-5 text-lg font-normal text-gray-500 dark:text-gray-400">
                            Are you sure you want to delete the exam "<strong>{examToDelete?.name}</strong>"? This action cannot be undone.
                        </h3>
                        <div className="flex justify-center gap-4">
                            <Button color="failure" disabled={isDeleting} onClick={handleDeleteExam}>
                                {isDeleting ? <Spinner size="sm" className="mr-2" /> : null}
                                Yes, I'm sure
                            </Button>
                            <Button color="gray" disabled={isDeleting} onClick={() => setIsDeleteModalOpen(false)}>
                                No, cancel
                            </Button>
                        </div>
                    </div>
                </Modal.Body>
            </Modal>
        </div>
    )
}

export default ExamListComponent

interface TransferExamDialogProps {
    isOpen: boolean;
    onClose: () => void;
    exam: QExam | null;
    onTransferSuccess: () => void;
}

const TransferExamDialog: React.FC<TransferExamDialogProps> = ({ isOpen, onClose, exam, onTransferSuccess }) => {
    const [step, setStep] = useState(1);
    const [selectedInstitution, setSelectedInstitution] = useState<AdminSchool | null>(null);
    const [selectedGrade, setSelectedGrade] = useState<{ id: number; name: string } | null>(null);
    const [selectedSubject, setSelectedSubject] = useState<{ id: number; name: string } | null>(null);
    const [selectedField, setSelectedField] = useState<AdminField | null>(null);
    const [selectedAcademicYear, setSelectedAcademicYear] = useState<AdminAcademicYear | null>(null);
    const [replicateChapters, setReplicateChapters] = useState(true);
    const [replicateQuestions, setReplicateQuestions] = useState(true);
    const [newGradeName, setNewGradeName] = useState('');
    const [newSubjectName, setNewSubjectName] = useState('');
    const [newFieldName, setNewFieldName] = useState('');
    const [newAcademicYearName, setNewAcademicYearName] = useState('');

    const [institutions, setInstitutions] = useState<AdminSchool[]>([]);
    const [grades, setGrades] = useState<{ id: number; name: string }[]>([]);
    const [subjects, setSubjects] = useState<{ id: number; name: string }[]>([]);
    const [fields, setFields] = useState<AdminField[]>([]);
    const [academicYears, setAcademicYears] = useState<AdminAcademicYear[]>([]);

    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const isSchoolType = selectedInstitution?.creator_type_name?.toLowerCase() === 'school';
    const isUniversityCollegeType = selectedInstitution && (selectedInstitution.creator_type_name?.toLowerCase() === 'university' || selectedInstitution.creator_type_name?.toLowerCase() === 'college');

    useEffect(() => {
        if (isOpen) {
            setStep(1);
            setSelectedInstitution(null);
            setSelectedGrade(null);
            setSelectedSubject(null);
            setSelectedField(null);
            setSelectedAcademicYear(null);
            setReplicateChapters(true);
            setReplicateQuestions(true);
            setNewGradeName('');
            setNewSubjectName('');
            setNewFieldName('');
            setNewAcademicYearName('');
            setError(null);
            // Fetch institutions for step 1
            const fetchInstitutions = async () => {
                setIsLoading(true);
                try {
                    const allSchoolsResponse = await adminGetSchools({}); // Fetch all schools
                    const allSchools = allSchoolsResponse.results;

                    let filteredSchools: AdminSchool[] = [];
                    if (exam?.institution_name) {
                        // Find the source institution to get its type
                        const sourceInstitution = allSchools.find(s => s.name === exam.institution_name);
                        if (sourceInstitution?.creator_type_name) {
                            const sourceInstitutionType = sourceInstitution.creator_type_name.toLowerCase();
                            filteredSchools = allSchools.filter(s => s.creator_type_name?.toLowerCase() === sourceInstitutionType);
                        } else {
                            // Fallback if source institution type cannot be determined
                            filteredSchools = allSchools;
                        }
                    } else {
                        // If source exam has no institution name, show all schools
                        filteredSchools = allSchools;
                    }
                    setInstitutions(filteredSchools);
                } catch (err: any) {
                    setError('Failed to load institutions.');
                    console.error('Error fetching institutions:', err);
                } finally {
                    setIsLoading(false);
                }
            };
            fetchInstitutions();
        }
    }, [isOpen, exam]);

    const handleNext = () => setStep(prev => prev + 1);
    const handleBack = () => setStep(prev => prev - 1);

    const handleInstitutionSelect = async (inst: AdminSchool) => {
        setSelectedInstitution(inst);
        setError(null);
        setIsLoading(true);
        try {
            if (inst.creator_type_name?.toLowerCase() === 'school') {
                const fetchedGrades = await adminGetSchoolGrades(inst.id);
                setGrades(fetchedGrades);
                setFields([]);
                setAcademicYears([]);
            } else if (inst.creator_type_name?.toLowerCase() === 'university' || inst.creator_type_name?.toLowerCase() === 'college') {
                const fetchedFields = await adminGetInstitutionFields(inst.id);
                setFields(fetchedFields);
                setGrades([]);
                setSubjects([]);
            }
            handleNext();
        } catch (err: any) {
            setError('Failed to load structure for selected institution.');
            console.error('Error fetching institution structure:', err);
        } finally {
            setIsLoading(false);
        }
    };

    const handleGradeSelect = async (grade: { id: number; name: string }) => {
        setSelectedGrade(grade);
        setError(null);
        setIsLoading(true);
        try {
            if (selectedInstitution) {
                const fetchedSubjects = await adminGetGradeSubjects(selectedInstitution.id, grade.id);
                setSubjects(fetchedSubjects);
            }
            // Removed handleNext() here to prevent premature advancement
        } catch (err: any) {
            setError('Failed to load subjects for selected grade.');
            console.error('Error fetching subjects:', err);
        } finally {
            setIsLoading(false);
        }
    };

    const handleFieldSelect = async (field: AdminField) => {
        setSelectedField(field);
        setError(null);
        setIsLoading(true);
        try {
            if (selectedInstitution) {
                const fetchedYears = await adminGetFieldAcademicYears(selectedInstitution.id, field.id);
                setAcademicYears(fetchedYears);
            }
            handleNext();
        } catch (err: any) {
            setError('Failed to load academic years for selected field.');
            console.error('Error fetching academic years:', err);
        } finally {
            setIsLoading(false);
        }
    };

    const handleTransfer = async () => {
        if (!exam || !selectedInstitution) {
            setError('Exam or target institution not selected.');
            return;
        }

        setIsLoading(true);
        setError(null);

        let targetSubjectId: number | undefined;
        let targetCourseId: number | undefined;

        if (isSchoolType) {
            if (!selectedSubject) {
                setError('Please select a subject.');
                setIsLoading(false);
                return;
            }
            targetSubjectId = selectedSubject.id;
        } else if (isUniversityCollegeType) {
            if (!selectedAcademicYear) {
                setError('Please select an academic year.');
                setIsLoading(false);
                return;
            }
            targetCourseId = selectedAcademicYear.id; // Assuming academic year ID can be used as course ID for transfer
        }

        try {
            await adminExamTransfer(exam.id, {
                target_institution_id: selectedInstitution.id,
                school_subject: targetSubjectId,
                course: targetCourseId,
                replicate_chapters: replicateChapters,
                replicate_questions: replicateQuestions,
            });
            onTransferSuccess();
        } catch (err: any) {
            setError(`Transfer failed: ${(err as any)?.response?.data?.detail || (err as any)?.message}`);
            console.error('Error transferring exam:', err);
        } finally {
            setIsLoading(false);
        }
    };

    const handleCreateGrade = async () => {
        if (!selectedInstitution || !newGradeName.trim()) return;
        setIsLoading(true);
        setError(null);
        try {
            const newG = await adminCreateGrade(selectedInstitution.id, newGradeName.trim());
            setGrades(prev => [...prev, newG]);
            setSelectedGrade(newG);
            setNewGradeName('');
            handleNext();
        } catch (err: any) {
            setError(`Failed to create grade: ${(err as any)?.response?.data?.detail || (err as any)?.message}`);
        } finally {
            setIsLoading(false);
        }
    };

    const handleCreateSubject = async () => {
        if (!selectedInstitution || !selectedGrade || !newSubjectName.trim()) return;
        setIsLoading(true);
        setError(null);
        try {
            const newS = await adminCreateSubject(selectedInstitution.id, selectedGrade.id, newSubjectName.trim());
            setSubjects(prev => [...prev, newS]);
            setSelectedSubject(newS);
            setNewSubjectName('');
            handleNext();
        } catch (err: any) {
            setError(`Failed to create subject: ${(err as any)?.response?.data?.detail || (err as any)?.message}`);
        } finally {
            setIsLoading(false);
        }
    };

    const handleCreateField = async () => {
        if (!selectedInstitution || !newFieldName.trim()) return;
        setIsLoading(true);
        setError(null);
        try {
            const newF = await adminCreateField(selectedInstitution.id, newFieldName.trim());
            setFields(prev => [...prev, newF]);
            setSelectedField(newF);
            setNewFieldName('');
            handleNext();
        } catch (err: any) {
            setError(`Failed to create field of study: ${(err as any)?.response?.data?.detail || (err as any)?.message}`);
        } finally {
            setIsLoading(false);
        }
    };

    const handleCreateAcademicYear = async () => {
        if (!selectedInstitution || !selectedField || !newAcademicYearName.trim()) return;
        setIsLoading(true);
        setError(null);
        try {
            const newY = await adminCreateAcademicYear(selectedInstitution.id, selectedField.id, newAcademicYearName.trim());
            setAcademicYears(prev => [...prev, newY]);
            setSelectedAcademicYear(newY);
            setNewAcademicYearName('');
            handleNext();
        } catch (err: any) {
            setError(`Failed to create academic year: ${(err as any)?.response?.data?.detail || (err as any)?.message}`);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <Modal show={isOpen} onClose={onClose} size="4xl" className="z-[9999]">
            <Modal.Header>Transfer Exam: {exam?.name}</Modal.Header>
            <Modal.Body>
                {isLoading && (
                    <div className="flex justify-center items-center p-4">
                        <Spinner size="lg" /> <span className="ml-2">Loading...</span>
                    </div>
                )}
                {error && (
                    <div className="p-3 mb-4 text-sm text-red-800 rounded-lg bg-red-50 dark:bg-gray-800 dark:text-red-400" role="alert">
                        {error}
                    </div>
                )}

                {step === 1 && (
                    <div>
                        <h3 className="text-lg font-bold mb-4">Step 1: Select Target Institution</h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 max-h-80 overflow-y-auto">
                            {institutions.length === 0 && !isLoading ? (
                                <p className="col-span-full text-center text-gray-500">No institutions available.</p>
                            ) : (
                                institutions.map(inst => (
                                    <div
                                        key={inst.id}
                                        className={`p-4 border rounded-lg cursor-pointer ${selectedInstitution?.id === inst.id ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20' : 'border-gray-200 dark:border-gray-700'}`}
                                        onClick={() => handleInstitutionSelect(inst)}
                                    >
                                        <p className="font-semibold">{inst.name}</p>
                                        <p className="text-sm text-gray-500">{inst.creator_type_name}</p>
                                    </div>
                                ))
                            )}
                        </div>
                        <div className="flex justify-end gap-2 mt-4">
                            <Button color="light" onClick={onClose}>Cancel</Button>
                            <Button onClick={handleNext} disabled={!selectedInstitution || isLoading}>Next <HiOutlineArrowRight className="ml-2 h-5 w-5" /></Button>
                        </div>
                    </div>
                )}

                {step === 2 && selectedInstitution && (
                    <div>
                        <h3 className="text-lg font-bold mb-4">Step 2: Select Target {isSchoolType ? 'Grade & Subject' : 'Field of Study & Academic Year'}</h3>

                        {isSchoolType ? (
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <Label htmlFor="select-grade" value="Select Grade" className="mb-2" />
                                    <Select id="select-grade" title="Select a grade" value={selectedGrade?.id || ''} onChange={async (e: React.ChangeEvent<HTMLSelectElement>) => {
                                        const gradeId = Number(e.target.value);
                                        const grade = grades.find(g => g.id === gradeId);
                                        if (grade) {
                                            await handleGradeSelect(grade);
                                        } else {
                                            setSelectedGrade(null);
                                            setSubjects([]);
                                        }
                                    }}>
                                        <option value="">Select a grade</option>
                                        {grades.map(g => (
                                            <option key={g.id} value={g.id}>{g.name}</option>
                                        ))}
                                    </Select>
                                    <div className="mt-4">
                                        <Label htmlFor="new-grade-name" value="Or Create New Grade" className="mb-2" />
                                        <TextInput id="new-grade-name" title="New Grade Name" value={newGradeName} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNewGradeName(e.target.value)} placeholder="New Grade Name" />
                                        <Button onClick={handleCreateGrade} disabled={!newGradeName.trim() || isLoading} className="mt-2">Create Grade</Button>
                                    </div>
                                </div>
                                <div>
                                    <Label htmlFor="select-subject" value="Select Subject" className="mb-2" />
                                    <Select id="select-subject" title="Select a subject" value={selectedSubject?.id || ''} onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                                        const subjectId = Number(e.target.value);
                                        const subject = subjects.find(s => s.id === subjectId);
                                        setSelectedSubject(subject || null);
                                    }} disabled={!selectedGrade}>
                                        <option value="">Select a subject</option>
                                        {subjects.map(s => (
                                            <option key={s.id} value={s.id}>{s.name}</option>
                                        ))}
                                    </Select>
                                    <div className="mt-4">
                                        <Label htmlFor="new-subject-name" value="Or Create New Subject" className="mb-2" />
                                        <TextInput id="new-subject-name" title="New Subject Name" value={newSubjectName} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNewSubjectName(e.target.value)} placeholder="New Subject Name" disabled={!selectedGrade} />
                                        <Button onClick={handleCreateSubject} disabled={!newSubjectName.trim() || !selectedGrade || isLoading} className="mt-2">Create Subject</Button>
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <Label htmlFor="select-field" value="Select Field of Study" className="mb-2" />
                                    <Select id="select-field" title="Select a field of study" value={selectedField?.id || ''} onChange={async (e: React.ChangeEvent<HTMLSelectElement>) => {
                                        const fieldId = Number(e.target.value);
                                        const field = fields.find(f => f.id === fieldId);
                                        if (field) {
                                            await handleFieldSelect(field);
                                        } else {
                                            setSelectedField(null);
                                            setAcademicYears([]);
                                        }
                                    }}>
                                        <option value="">Select a field</option>
                                        {fields.map(f => (
                                            <option key={f.id} value={f.id}>{f.name}</option>
                                        ))}
                                    </Select>
                                    <div className="mt-4">
                                        <Label htmlFor="new-field-name" value="Or Create New Field" className="mb-2" />
                                        <TextInput id="new-field-name" title="New Field Name" value={newFieldName} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNewFieldName(e.target.value)} placeholder="New Field Name" />
                                        <Button onClick={handleCreateField} disabled={!newFieldName.trim() || isLoading} className="mt-2">Create Field</Button>
                                    </div>
                                </div>
                                <div>
                                    <Label htmlFor="select-academic-year" value="Select Academic Year" className="mb-2" />
                                    <Select id="select-academic-year" title="Select an academic year" value={selectedAcademicYear?.id || ''} onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                                        const yearId = Number(e.target.value);
                                        const year = academicYears.find(y => y.id === yearId);
                                        setSelectedAcademicYear(year || null);
                                    }} disabled={!selectedField}>
                                        <option value="">Select an academic year</option>
                                        {academicYears.map(y => (
                                            <option key={y.id} value={y.id}>{y.name}</option>
                                        ))}
                                    </Select>
                                    <div className="mt-4">
                                        <Label htmlFor="new-academic-year-name" value="Or Create New Academic Year" className="mb-2" />
                                        <TextInput id="new-academic-year-name" title="New Academic Year Name" value={newAcademicYearName} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNewAcademicYearName(e.target.value)} placeholder="New Academic Year Name" disabled={!selectedField} />
                                        <Button onClick={handleCreateAcademicYear} disabled={!newAcademicYearName.trim() || !selectedField || isLoading} className="mt-2">Create Academic Year</Button>
                                    </div>
                                </div>
                            </div>
                        )}

                        <div className="flex justify-between gap-2 mt-4">
                            <Button color="light" onClick={handleBack}>Back</Button>
                            <Button onClick={handleNext} disabled={isLoading || (!!isSchoolType && !selectedSubject) || (!!isUniversityCollegeType && !selectedAcademicYear)}>Next <HiOutlineArrowRight className="ml-2 h-5 w-5" /></Button>
                        </div>
                    </div>
                )}

                {step === 3 && selectedInstitution && exam && (
                    <div>
                        <h3 className="text-lg font-bold mb-4">Step 3: Confirm Transfer</h3>
                        <div className="space-y-2 mb-4">
                            <p><strong>Exam:</strong> {exam.name}</p>
                            <p><strong>From Institution:</strong> {exam.institution_name || 'N/A'}</p>
                            <p><strong>To Institution:</strong> {selectedInstitution.name} ({selectedInstitution.creator_type_name})</p>
                            {isSchoolType && selectedGrade && selectedSubject && (
                                <p><strong>To Grade/Subject:</strong> {selectedGrade.name} / {selectedSubject.name}</p>
                            )}
                            {isUniversityCollegeType && selectedField && selectedAcademicYear && (
                                <p><strong>To Field/Academic Year:</strong> {selectedField.name} / {selectedAcademicYear.name}</p>
                            )}
                        </div>

                        <div className="flex items-center mb-2">
                            <input
                                type="checkbox"
                                id="replicate-chapters"
                                title="Replicate Chapters"
                                checked={replicateChapters}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setReplicateChapters(e.target.checked)}
                                className="mr-2"
                            />
                            <Label htmlFor="replicate-chapters">Replicate Chapters</Label>
                        </div>
                        <div className="flex items-center mb-4">
                            <input
                                type="checkbox"
                                id="replicate-questions"
                                title="Replicate Questions"
                                checked={replicateQuestions}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setReplicateQuestions(e.target.checked)}
                                className="mr-2"
                            />
                            <Label htmlFor="replicate-questions">Replicate Questions</Label>
                        </div>

                        <div className="flex justify-between gap-2 mt-4">
                            <Button color="light" onClick={handleBack}>Back</Button>
                            <Button onClick={handleTransfer} disabled={isLoading}><HiOutlineDocumentDuplicate className="mr-2 h-5 w-5" /> Confirm Transfer</Button>
                        </div>
                    </div>
                )}
            </Modal.Body>
        </Modal>
    );
};
